import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { ICON_STROKE } from "../ui/icon";
import ScanConsentIllustration from "./ScanConsentIllustration";

const TRUST_KEYS = ["scanConsentTrust1", "scanConsentTrust2", "scanConsentTrust3"];

const ScanConsentModal = ({
  open,
  phase = "idle",
  onShare,
  onDismiss,
  t,
}) => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const dialogRef = useRef(null);
  const primaryRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setShowPrivacy(false);
      return undefined;
    }

    const onKey = (e) => {
      // Escape = continue without location (same as secondary CTA), not a silent close.
      if (e.key === "Escape") {
        if (phase === "idle") onDismiss?.();
        return;
      }
      if (e.key === "Enter" && phase === "idle" && document.activeElement?.tagName !== "BUTTON") {
        e.preventDefault();
        onShare?.();
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => primaryRef.current?.focus(), 50);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.clearTimeout(focusTimer);
    };
  }, [open, phase, onDismiss, onShare]);

  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;

    const dialog = dialogRef.current;
    const focusable = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const trapTab = (e) => {
      if (e.key !== "Tab" || focusable.length === 0) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener("keydown", trapTab);
    return () => dialog.removeEventListener("keydown", trapTab);
  }, [open, phase, showPrivacy]);

  if (!open) return null;

  const busy = phase === "loading" || phase === "success";

  return (
    <div
      className="scan-consent-overlay"
      // Blocking gate: only Share / Continue without location dismisses — not backdrop tap.
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="scan-consent-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scan-consent-title"
        aria-describedby="scan-consent-desc"
        onClick={(e) => e.stopPropagation()}
      >
        {phase === "success" ? (
          <div className="scan-consent-status">
            <div className="scan-consent-success__icon" aria-hidden>
              <Check size={24} strokeWidth={ICON_STROKE} />
            </div>
            <h2 id="scan-consent-title" className="scan-consent-success__title">
              {t("public.scanConsentSuccessTitle")}
            </h2>
            <p className="scan-consent-success__text">{t("public.scanConsentSuccessBody")}</p>
            <p className="scan-consent-success__next">{t("public.scanConsentSuccessNext")}</p>
          </div>
        ) : phase === "loading" ? (
          <div className="scan-consent-status">
            <Loader2 size={28} className="scan-consent-loading__spinner animate-spin" aria-hidden />
            <p id="scan-consent-title" className="scan-consent-loading__text">
              {t("public.scanConsentLoading")}
            </p>
          </div>
        ) : (
          <>
            <div className="scan-consent-card__scroll">
              <ScanConsentIllustration className="scan-consent-card__illustration scan-consent-card__illustration--desktop" />

              <h2 id="scan-consent-title" className="scan-consent-card__title">
                {t("public.scanConsentTitle")}
              </h2>
              <p className="scan-consent-card__subtitle scan-consent-card__subtitle--desktop">
                {t("public.scanConsentSubtitle")}
              </p>
              <p id="scan-consent-desc" className="scan-consent-card__description">
                {t("public.scanConsentDescription")}
                <span className="scan-consent-card__note-inline">
                  {" "}
                  {t("public.scanConsentNote")}
                </span>
              </p>

              <ul className="scan-consent-trust" aria-label="Privacy assurances">
                {TRUST_KEYS.map((key) => (
                  <li key={key} className="scan-consent-trust__item">
                    <span className="scan-consent-trust__icon" aria-hidden>
                      <Check size={12} strokeWidth={ICON_STROKE} />
                    </span>
                    <span>{t(`public.${key}`)}</span>
                  </li>
                ))}
              </ul>

              <p className="scan-consent-card__human scan-consent-card__human--desktop">
                {t("public.scanConsentHumanNote")}
              </p>

              <div className="scan-consent-meta">
                <button
                  type="button"
                  className="scan-consent-privacy-toggle"
                  onClick={() => setShowPrivacy((v) => !v)}
                  aria-expanded={showPrivacy}
                >
                  {t("public.scanConsentPrivacyLink")}
                  {showPrivacy ? (
                    <ChevronUp size={14} strokeWidth={ICON_STROKE} aria-hidden />
                  ) : (
                    <ChevronDown size={14} strokeWidth={ICON_STROKE} aria-hidden />
                  )}
                </button>
                {showPrivacy && (
                  <div
                    className="scan-consent-privacy"
                    role="region"
                    aria-label={t("public.scanConsentPrivacyTitle")}
                  >
                    <p className="scan-consent-privacy__text">{t("public.scanConsentPrivacyBody")}</p>
                  </div>
                )}

                <p className="scan-consent-footer">
                  {t("public.scanConsentCall108")}{" "}
                  <a href="tel:108" className="scan-consent-footer__link">
                    {t("public.scanConsentCall108Link")} →
                  </a>
                </p>
              </div>
            </div>

            <div className="scan-consent-card__footer">
              <div className="scan-consent-actions">
                <button
                  ref={primaryRef}
                  type="button"
                  className="scan-consent-btn scan-consent-btn--primary"
                  onClick={onShare}
                  disabled={busy}
                >
                  <MapPin size={18} strokeWidth={ICON_STROKE} aria-hidden />
                  {t("public.scanConsentConfirm")}
                </button>
                <button
                  type="button"
                  className="scan-consent-btn scan-consent-btn--secondary"
                  onClick={onDismiss}
                  disabled={busy}
                >
                  {t("public.scanConsentSecondary")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScanConsentModal;
